import { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";
import { DarkTheme, LightTheme } from "../themes";
import { Theme, ThemeProvider, createTheme } from "@mui/material";
import { Box } from "@mui/system";
import { IMaterialUi } from "../interfaces/IMaterialUi";
import { useAutenticacaoContext } from "./AutenticacaoContext";
import { ApiFactory } from "../config/ApiFactory";
import { useLocation } from "react-router-dom";
import { getCache, setCache } from "../services/indexedDBCache";

interface IThemeContext {
    themeName: 'light' | 'dark';
    toggleTheme: () => void;
    theme: IMaterialUi;
    logo: string;
    customTheme: boolean;
    refreshTemas: () => Promise<void>;
}

export const useThemeContext = () => {
    return useContext(ThemeContext)
}

const baseTheme = {
    components: {
        MuiAccordion: {
            styleOverrides: {
              root: {
                borderRadius: 6
              },
            },
        },
    },
    shape: {
        borderRadius: 10
    },
    shadows: [
        'none',
        '0px 1px 3px rgba(0, 0, 0, 0.08)', 
        '0px 2px 4px rgba(0, 0, 0, 0.08)', 
        '0px 3px 6px rgba(0, 0, 0, 0.08)', 
        '0px 4px 8px rgba(0, 0, 0, 0.08)', 
        '0px 6px 12px rgba(0, 0, 0, 0.08)', 
        '0px8px 16px rgba(0, 0, 0, 0.08)', 
        '0px 10px 20px rgba(0, 0, 0, 0.08)', 
        '0px 14px 28px rgba(0, 0, 0, 0.08)', 
        '0px 18px 36px rgba(0, 0, 0, 0.08)', 
        'none', 
        'none', 
        'none', 
        'none', 
        'none', 
        'none', 
        'none', 
        'none', 
        'none', 
        'none', 
        'none', 
        'none', 
        'none', 
        'none', 
        'none', 
    ], 
}

export const ThemeContext = createContext({} as IThemeContext)

export const AppThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { autenticado, estabelecimento } = useAutenticacaoContext();
    const apiEstabelecimentos = ApiFactory.getApi("MS_ESTABELECIMENTOS");
    
    // Recuperar tema do localStorage ou usar 'light' como padrão
    const [themeName, setThemeName] = useState<'light' | 'dark'>(() => {
        const savedTheme = localStorage.getItem('theme-preference');
        return (savedTheme === 'dark' ? 'dark' : 'light') as 'light' | 'dark';
    });
    
    const [lightTheme, setLightTheme] = useState<Theme>(LightTheme);
    const [darkTheme, setDarkTheme] = useState<Theme>(DarkTheme);
    const [lightLogo, setLightLogo] = useState<string>(`${process.env.PUBLIC_URL}/images/logo.png`);
    const [darkLogo, setDarkLogo] = useState<string>(`${process.env.PUBLIC_URL}/images/logo.png`);
    const [customTheme, setCustomTheme] = useState(false);

    const toggleTheme = useCallback(() => {
        setThemeName(oldThemeName => {
            const newTheme = oldThemeName === 'light' ? 'dark' : 'light';
            // Salvar no localStorage
            localStorage.setItem('theme-preference', newTheme);
            return newTheme;
        });
    }, []) 

    const theme = useMemo(() => {
        if (themeName === 'dark') return darkTheme;

        return lightTheme;
    }, [themeName, lightTheme, darkTheme])

    const logo = useMemo(() => {
        if (themeName === 'dark') return darkLogo;

        return lightLogo;
    }, [themeName, lightLogo, darkLogo])

    const buscarTemas = async () => {
        if (autenticado && estabelecimento) {
            const cacheKeyLight = `tema-light-${estabelecimento.uuid}`;
            const cacheKeyDark = `tema-dark-${estabelecimento.uuid}`;

            try {
                // ========== BUSCAR TEMA LIGHT ==========
                let light = await getCache(cacheKeyLight);
                
                // undefined = não há cache, buscar da API
                // null = cache negativo (tema não existe)
                // ITema = tema existe no cache
                if (light === undefined) {
                    try {
                        const response = await apiEstabelecimentos.get("/tema/light");
                        light = response.data;
                        
                        // Salvar no cache se houver dados válidos
                        if (light && light.materialUi) {
                            await setCache(cacheKeyLight, light, estabelecimento.uuid);
                        } else {
                            // Salvar cache negativo (tema não existe ou está vazio)
                            await setCache(cacheKeyLight, null, estabelecimento.uuid);
                        }
                    } catch (error: any) {
                        if (error?.response?.status === 404) {
                            // Salvar cache negativo (tema não existe)
                            await setCache(cacheKeyLight, null, estabelecimento.uuid);
                        }
                        light = null;
                    }
                }

                if(light && light.materialUi) {
                    setCustomTheme(true);
                    setLightTheme(createTheme({
                        ...light.materialUi,
                        // Forçar configurações do baseTheme (shape, shadows, components)
                        shape: baseTheme.shape,
                        shadows: baseTheme.shadows,
                        components: {
                            ...(light.materialUi as any).components,
                            ...baseTheme.components,
                        }
                    } as any));
                    const logo = light.imagens?.find((imagem: any) => imagem.tipo == "Logo");
                    if (logo) {
                        setLightLogo(logo.url);
                    }
                }

                // ========== BUSCAR TEMA DARK ==========
                let dark = await getCache(cacheKeyDark);
                
                if (dark === undefined) {
                    try {
                        const response = await apiEstabelecimentos.get("/tema/dark");
                        dark = response.data;
                        
                        // Salvar no cache se houver dados válidos
                        if (dark && dark.materialUi) {
                            await setCache(cacheKeyDark, dark, estabelecimento.uuid);
                        } else {
                            // Salvar cache negativo (tema não existe ou está vazio)
                            await setCache(cacheKeyDark, null, estabelecimento.uuid);
                        }
                    } catch (error: any) {
                        if (error?.response?.status === 404) {
                            // Salvar cache negativo (tema não existe)
                            await setCache(cacheKeyDark, null, estabelecimento.uuid);
                        }
                        dark = null;
                    }
                }

                if(dark && dark.materialUi) {
                    setCustomTheme(true);
                    setDarkTheme(createTheme({
                        ...dark.materialUi,
                        // Forçar configurações do baseTheme (shape, shadows, components)
                        shape: baseTheme.shape,
                        shadows: baseTheme.shadows,
                        components: {
                            ...(dark.materialUi as any).components,
                            ...baseTheme.components,
                        }
                    } as any));
                    const logo = dark.imagens?.find((imagem: any) => imagem.tipo == "Logo");
                    if (logo) {
                        setDarkLogo(logo.url);
                    }
                }
            } catch (error) {
                console.error('Erro ao buscar temas:', error);
            }
        }
    }

    useEffect(() => {
        buscarTemas();
    }, [autenticado, estabelecimento])

    const location = useLocation();

    const possuiHeader = useMemo(() => {
        const urlsExcessoes = ['/login', '/criar-conta', '/', '/recuperar-senha', '/pagina-nao-encontrada', '/nao-autorizado'];
        return !urlsExcessoes.includes(location.pathname);
    }, [location.pathname]);

    return (
        <ThemeContext.Provider value={{ customTheme, logo, themeName, toggleTheme, theme: theme as IMaterialUi, refreshTemas: buscarTemas }}>
            <ThemeProvider theme={theme as IMaterialUi}>
                <Box
                    marginTop={!possuiHeader ? "0px" : "50px"}
                    width={'100%'}
                    bgcolor={theme.palette.background.default}
                    minHeight={"calc(100vh - 50px)"}
                    height={!possuiHeader ? "100%" : "calc(100% - 50px)"}
                    padding={0}
                    color={theme.palette.text.primary}>
                    {children}
                </Box>

            </ThemeProvider>
        </ThemeContext.Provider>
    );
} 